import { DebouncedFunc } from 'lodash';
import { PickerFileMetadata } from 'filestack-js';
import { MentionItem } from 'react-mentions';
import {
  CUSTOM_FIELDS_FILTERS,
  DELETE_PROGRAM_OPTIONS,
  LICENSE_TIER,
  MENTIONS_TYPES,
  NOTIFICATION_TYPE,
  PROJECT_CONTENT_TYPE,
  PROJECT_HEALTH,
  PROJECT_IMPORT_ATTRIBUTES,
  PROJECT_PARTICIPANT_TYPE,
  PROJECT_PRIORITY,
  PROJECT_STATUS,
  PROJECT_USER_ROLE,
  PROJECTS_BOARD_TABS,
  PROJECTS_LIST_VIEW_MODE,
  PROJECTS_TABLE_FILTERS,
  PROJECTS_TABLE_TABS,
  QUESTIONNAIRE_TYPE,
  REQUEST_LD_PRIORITY,
  REQUEST_REQUIRED_FIELDS,
  REQUEST_SECTIONS,
  REQUEST_TYPE,
  REQUESTS_TABLE,
  SLICE_STATUS,
  TABLE_EXPORT_OPTIONS,
  TASK_STATUS,
  TASKS_TABLE_TABS,
  TIME_OFF_TYPE,
  UPDATE_PROJECT_TABS,
  USER_ROLES,
  USER_STATUS,
} from 'utils/constants';
import {
  DashboardUserAffordance,
  INSIGHT_IDS,
} from 'utils/constants/dashboard';
import { ReactNode } from 'react';
import { ProjectTemplateField } from 'utils/types/templates';
import type { LearningTeam, TeamsManaged } from './types/learningTeam';
import type { BusinessTeam } from './types/businessTeams';
import { Mentions } from './types/comments';
import {
  InsightCatalogue,
  InsightCategory,
} from 'types/store/normalized/insights/InsightCatalogue';
import { ChartType } from 'types/store/normalized/insights/chartTypes';
import { DashboardFilters } from 'utils/types/dashboardFilters';
import type { RequestPriority } from 'utils/types/request';

export type objKeyAsString = {
  [key: string]: any;
};

export type IdentifierFormat = {
  BASE: string;
  FULL: string;
};

export type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

export type requestType = typeof REQUEST_TYPE[number];

export type RequestsTable = typeof REQUESTS_TABLE[keyof typeof REQUESTS_TABLE];

export type Owner = {
  id?: string;
  organization_id?: string;
  ldTeam_id?: string;
  businessTeam_id?: string;
  type?: string;
  status?: string;
  note?: string;
  default_capacity?: number;
  avatar_url?: string;
  data?: {
    role?: string;
    email?: string;
    jobTitle?: string;
    lastName?: string;
    firstName?: string;
    jobFunctions?: string[];
    languages?: string[];
    skills?: string;
  };
};

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  province?: string;
  profilePicture?: string;
  files?: string;
}

interface LDUserData extends UserData {
  jobFunctions?: string[];
  employmentType?: string;
  skills?: string;
  rateHour?: string;
  languages?: string[];
  files?: string;
}

interface ExternalUserData extends UserData {
  companyName?: string;
  rateHour?: string;
  files?: string;
}

export type SystemUserTypes = 'ld' | 'business' | 'external' | 'facilitator';

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

export interface User {
  id: string;
  country_iso_3166_1_alpha_2_code: string;
  type: SystemUserTypes;
  role: UserRole;
  data: UserData;
  status: typeof USER_STATUS[keyof typeof USER_STATUS];
  disabled_at?: string | null;
  avatar_url?: string;
  organization_id?: string;
  notified_user?: null | boolean;
  email?: string;
}
export interface FinancialSettings {
  currency?: string;
  timeFrame?: string;
  fiscalPeriodStart?: string;
}

export enum TimeFrame {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

export enum BudgetStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export type BudgetModel = {
  id: string;
  organizationId: string;
  title: string;
  description?: string;
  currency: string;
  fiscalPeriodStart: string;
  timeFrame: TimeFrame;
  status: BudgetStatus;
  totalTarget?: number;
  totalActuals?: number;
  totalAllocated?: number;
};

export type BudgetAmount = {
  id: string;
  organizationId: string;
  budgetId: string;
  month: string;
  target?: number;
};

export type CreateBudgetRequest = {
  title: string;
  fiscalPeriodStart: string;
};

export type DuplicateBudgetRequest = {
  id: string;
  title: string;
  fiscalPeriodStart: string;
  carryActual: boolean;
};

export type BudgetStatusChangeRequest = {
  id: string;
  status: BudgetStatus;
};

export type LineItem = {
  id: string;
  organizationId: string;
  budgetId: string;
  budgetSource?: BudgetSourceEnum;
  description?: string;
  programId?: string;
  projectId?: string;
  learningTeamId?: string;
  vendorId?: string;
  currency: string;
  note?: string;
  hasFile?: boolean;
  showInput?: boolean;
};

export type LineItemAmount = {
  id: string;
  organizationId: string;
  lineItemId: string;
  month: string;
  monthIndex: number;
  allocation?: number;
  actual?: number;
};

export type LineItemWithAmounts = LineItem & {
  amounts: LineItemAmount[];
  rawAmounts: number[];
  rawActuals: number[];
};

export type LineItemFile = {
  id?: string;
  lineItemId: string;
  fileId?: string;
  fileName: string;
  fileType: string;
  fileUrl: string;
  handle: string;
  key: string;
};

export type CreateLineItemRequest = {
  id: string;
  budgetId: string;
  budgetSource?: string;
  description?: string;
  programId?: string;
  projectId?: string;
  learningTeamId?: string;
  vendorId?: string;
  // amounts
  amounts: number[];
};

export type UpdateBudgetAmountRequest = {
  budgetId: string;
  serial: number;
  target?: number;
};

export type UpdateFixedBrokenLineItemsRequest = {
  id: string;
  learningTeamId: string | undefined;
  programId: string | undefined;
  projectId: string | undefined;
};

export enum BudgetSourceEnum {
  LD_BUDGET = 'L&D Budget',
  BUSINESS_BUDGET = 'Business Budget',
  SPECIAL_FUNDING = 'Special Funding',
  MIXED = 'Mixed',
  OTHERS = 'Others',
}

export type CreateFileUploadRequest = {
  id?: string;
  key?: string;
  fileName: string;
  fileType: string;
  fileUrl: string;
  handle?: string;
  lineItemId: string;
};

export type ProjectToProgramMap = Record<string, Set<string>>;

export enum TimeOptionsEnum {
  FISCAL_YEAR_SHORT = 'FY',
  FISCAL_YEAR = 'Fiscal Year',
  Q1 = 'Q1',
  Q2 = 'Q2',
  Q3 = 'Q3',
  Q4 = 'Q4',
}

export type ProjectIdAndYearReq = {
  projectId: string;
  year: number;
};
export interface LDUser extends User {
  ldTeam_id?: string;
  registeredLearningTeams: LearningTeam[];
  default_capacity: number;
  public_holidays_in_capacity_enabled?: boolean;
  data: LDUserData;
  teamsManaged?: LearningTeam[];
  is_facilitator?: boolean;
}

export interface BusinessUser extends User {
  businessTeam_id?: string;
}

export interface ExternalUser extends User {
  data: ExternalUserData;
}

export interface FacilitatorUser extends User {
  ldTeam_id?: string;
  registeredLearningTeams?: LearningTeam[];
  default_capacity?: number;
  public_holidays_in_capacity_enabled?: boolean;
  data: LDUserData;
}

export type DashboardNames = 'intake' | 'project';

export type CurrentUser = {
  email?: string;
  full_name?: string;
  id?: string;
  organization_id?: string;
  type?: string;
  role?: UserRole;
  avatar_url?: string;
  firstName?: string;
  lastName?: string;
  status?: typeof USER_STATUS[keyof typeof USER_STATUS];
  teamsManaged?: TeamsManaged[];
  collaboratedProjects?: string[];
  v2ParticipatedProjects?: string[];
  businessTeam?: string;
  dashboardIds?: { [key in DashboardNames]: string };
  firstUser?: boolean;
  associatedTeamsCount?: number;
  defaultCapacity?: number;
  isFacilitator?: boolean;
  first_login_date?: string;
  last_login_date?: string;
  countryCode?: string;
};

export interface Request {
  updatedAt?: any;
  createdAt?: any;
  requester?: any;
  requester_id?: string;
  requestCreator?: any;
  request_creator_id?: string;
  reviewers?: any;
  owners?: LDUser[];
  businessTeams?: { id: string; title?: string }[];
  requestIdentifier?: string;
  submittedAt?: any;
  title?: string;
  description?: string;
  id?: string;
  data?: {
    baseFormId?: string;
    additionalInformation?: string;
    cancellation?: {
      reason?: string;
      details?: string;
      ldRemark?: string;
    };
    declination?: {
      reason?: string;
      details?: string;
      comments?: string;
    };
    approval?: {
      comments?: string;
    };
    waitlisted?: {
      conditions?: string[];
      details?: string;
      comments?: string;
    };
    published?: boolean;
  };
  request_type?: requestType;
  status?: string;
  effort?: string;
  cost?: string;
  ldPriority?: typeof REQUEST_LD_PRIORITY[keyof typeof REQUEST_LD_PRIORITY];
  compliance?: boolean;
  requestProjects?: Project[];
  businessTeam?: {
    id?: string;
    title?: string;
  };
  decision_date?: string;
  decisionDate?: string;
  type?: typeof QUESTIONNAIRE_TYPE[keyof typeof QUESTIONNAIRE_TYPE];
  organization_id?: string;
  executive_stakeholder?: string;
  form_description?: string;
  formTitle?: string;
  form_id?: string;
  form?: {
    title: string;
  };
  draft_public_setting?: boolean;
  isArchived?: boolean;
  archiveDate?: string;
  priority?: RequestPriority;
  ownerIds?: string[];
  isJsonSchema?: boolean;
}

export type RequestQuestionData = {
  value: any | string;
  label: string;
  isRequired?: boolean;
};

export type RequestQuestionDataDropdown = RequestQuestionData & {
  items: dropdownOption[];
  multiselect: boolean;
};

export type RequestQuestionDataDate = RequestQuestionData & {
  range: boolean;
};

export type RequestQuestionDataFile = RequestQuestionData & {
  placeholder?: string;
  maxFileSizeBytes?: number | null;
};

export interface RequestQuestion {
  id: string;
  data:
    | RequestQuestionData
    | RequestQuestionDataDropdown
    | RequestQuestionDataDate
    | RequestQuestionDataFile;
  section: typeof REQUEST_SECTIONS[keyof typeof REQUEST_SECTIONS];
  type: QuestionTypes;
  is_old_date_question?: boolean;
}

export type QuestionTypes =
  | 'radio'
  | 'checkbox'
  | 'range'
  | 'date'
  | 'dropdown'
  | 'number'
  | 'customTextArea'
  | 'url'
  | 'file'
  | 'toggle';

export interface intakeQuestionWrapper {
  question: RequestQuestion;
  handler: (
    question: any,
    value:
      | string
      | boolean
      | number
      | Array<PickerFileMetadata>
      | dropdownOption
      | dropdownOption[]
      | rangeDate,
    path: string
  ) => void | DebouncedFunc<
    (
      question: any,
      value: string | boolean | number | Array<PickerFileMetadata>,
      path: string
    ) => void
  >;
  disabled?: boolean;
}

export type UpdateReqData = {
  requestAttributes: Request;
  requestQuestions: { [key: string]: RequestQuestion };
};

export type RequestPageTabs = 0 | 1 | 2 | 3;

export type RequestRequiredFields =
  typeof REQUEST_REQUIRED_FIELDS[keyof typeof REQUEST_REQUIRED_FIELDS];

export type RequestRequiredErrors = PartialRecord<
  RequestRequiredFields,
  boolean
>;

export type LDUserType = {
  businessTeam_id: string;
  data: {
    email: string;
    lastName: string;
    firstName: string;
  };
  id: string;
  ldTeam_id: string;
};

export interface LDTeam {
  id: string;
  name: string;
  description: string;
}

export type FormOption = {
  label: string;
  value: string;
  requestType?: string | null;
  formDescription?: string;
  businessTeams?: { id: string; title?: string }[];
  published?: boolean;
};

export type rangeDate = {
  startDate: Date | string;
  endDate: Date | string;
  requestType?: string | null;
  formDescription?: string;
  businessTeams?: { id: string; title?: string }[];
  published?: boolean;
};

export type filter = {
  value: string | rangeDate | FormOption;
  operator: string;
  logic: 'AND' | 'OR' | undefined;
  type: string;
  column?: string;
};

export type PropertyCommentType = CommentType & {
  message: string;
  data?: {
    mentions?: ProjectCommentMentionsData;
  };
  commentCreator: {
    id: string;
    status: string;
    data: {
      firstName: string;
      lastName: string;
    };
  };
};

export type newPropertyCommentType = {
  message: string;
  isBaseComment: boolean;
  requestId: string;
  requestProperty: string;
  userId: string;
  data?: {
    mentions?: ProjectCommentMentionsData;
  };
};

export type QuestionCommentType = CommentType & {
  content: string;
  author: {
    userId: string;
    name?: string;
    status?: string;
  };
  commentThreadId?: string;
  data?: {
    mentions?: ProjectCommentMentionsData;
  };
};

interface CommentType {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export type ProjectComment = CommentType & {
  content: string;
  author?: {
    userId: string;
    name: string;
    status: string;
  };
  commentCreator: {
    id: string;
    data: {
      firstName: string;
      lastName: string;
    };
  };
  parentId?: string;
  projectId: string;
  commentCreatorId: string;
  data?: {
    mentions?: ProjectCommentMentionsData;
  };
};

export type ProjectCommentMentionsData = {
  value: string;
  plainTextValue: string;
  mentions: MentionItem[];
};

export interface ProjectOwner {
  data: {
    email?: string;
    lastName?: string;
    firstName?: string;
  };
  project_owners: {
    projectLearnOpId?: string;
    userId?: string;
  };
}

export interface ProjectCollaborator {
  data: {
    email?: string;
    lastName?: string;
    firstName?: string;
  };
  project_collaborators: {
    projectLearnOpId?: string;
    userId?: string;
  };
}

export interface ProjectParticipant {
  data: {
    email?: string;
    lastName?: string;
    firstName?: string;
  };
  project_participants: {
    projectLearnOpId?: string;
    userId?: string;
  };
}

export interface V2ProjectParticipant {
  data: {
    email?: string;
    lastName?: string;
    firstName?: string;
  };
  v2_project_participant: {
    project_id?: string;
    user_id?: string;
  };
}

export type ProjectHealth = typeof PROJECT_HEALTH[keyof typeof PROJECT_HEALTH];
export type ProjectPriority =
  typeof PROJECT_PRIORITY[keyof typeof PROJECT_PRIORITY];

export interface Project {
  id: string;
  projectNumber?: number;
  title: string;
  status?: typeof PROJECT_STATUS[keyof typeof PROJECT_STATUS];
  createdAt: string;
  owners: ProjectOwner[];
  collaborators: ProjectCollaborator[];
  participants: ProjectParticipant[];
  hold_reason: string;
  startDate: string;
  targetCompletionDate: string;
  actualDate?: string;
  budget_source?: string;
  resourcing_type?: string;
  health?: ProjectHealth;
  category_id: string;
  businessUnitId: string;
  priority?: typeof PROJECT_PRIORITY[keyof typeof PROJECT_PRIORITY];
  process_id?: string;
  stage_id?: string | null;
  actual_cost?: string;
  actual_time_spent?: string;
  finalComments?: string;
  closedDate?: string;
  tasksOrdering?: string[];
  businessTeams?: BusinessTeam[];
  privacy?: string;
  ldteams?: LearningTeam[];
  process?: string;
  category?: string;
  stage?: string;
  is_archived?: boolean;
  isProjectPartOfUserPrograms?: boolean;
}

export interface ProgramLinkedProject extends Project {
  number_of_completed_tasks: number;
  number_of_not_completed_tasks: number;
}

export interface NewProject extends objKeyAsString {
  title: string;
  businessUnit?: string;
  category_id?: string;
  description?: string;
  startDate: string | null;
  targetCompletionDate: string | null;
  targetLaunchDate?: string | null;
  actualDate?: string | null;
  process_id?: string;
  status?: string;
  stage_id?: string;
  privacy: string;
  priority?: string | null;
  owners: (string | undefined)[];
  resourcing_type?: string | null;
  estimatedEffort?: string;
  budget_source?: string | null;
  estimated_cost?: string;
  allocated_budget?: number;
  health?: string;
  hold_reason?: string;
  data?: object;
  organization_id?: String | undefined;
  actual_cost?: number;
  actual_time_spent?: number;
  finalComments?: string;
  closedDate?: string | null;
  tasksOrdering?: string[];
  collaborators?: ProjectCollaborator[];
  participants?: ProjectParticipant[];
  projectNumber?: string;
  businessTeams?: string[];
  learningTeams?: string[];
  project_template_id?: string;
  customProperties?: ProjectTemplateField[];
  is_archived?: boolean;
  isProjectPartOfUserPrograms?: boolean;
  v2_participants?: V2ProjectParticipant[];
  isJsonSchema?: boolean;
}

export type ImportingProject = Record<
  | typeof PROJECT_IMPORT_ATTRIBUTES[keyof typeof PROJECT_IMPORT_ATTRIBUTES]
  | string,
  string
>;

export interface ModalProps {
  actionButtons?: any[];
  closeModal?: (event?: React.SyntheticEvent<Element, Event>) => void;
  isOpen?: boolean;
  title?: string;
  size?: any;
  titleIcon?: any;
  children?: ReactNode;
  childrenClassName?: string;
}

export type MoreActionsOption = {
  value: string;
  label: string;
  dataCy?: string;
  iconName: string;
  iconSrc?: string;
  iconClassName?: string;
  textClassName?: string;
  selectOption?: () => void;
  disabled?: boolean;
  tooltip?: {
    text?: string;
    position?:
      | 'bottomRight'
      | 'bottomLeft'
      | 'topRight'
      | 'topLeft'
      | 'topCenter';
  };
};

export type LDTeamRow = {
  id: string;
  name: string;
  teamManager?: AvatarUser;
  parentTeam?: string;
  createdAt?: string;
};

export type SortingType = 'desc' | 'asc';

export type SortingOrderType = {
  order: SortingType;
  orderBy: string;
};

export type ProjectsTableTab =
  typeof PROJECTS_TABLE_TABS[keyof typeof PROJECTS_TABLE_TABS];

export interface UserOption extends Option {
  avatar: { initial?: string; imageSrc?: string };
}

export interface UserAvatars extends Option {
  avatar: { initial: string; imageSrc?: string; name?: string };
  disabled?: boolean;
}

export type ProjectUserRole =
  typeof PROJECT_USER_ROLE[keyof typeof PROJECT_USER_ROLE];

export type Notification = {
  id?: string;
  user_id?: string;
  status?: string;
  message?: string;
  data?: {};
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
};

export type Status = typeof SLICE_STATUS[keyof typeof SLICE_STATUS];
export type Task_Status = typeof TASK_STATUS[keyof typeof TASK_STATUS];

export type FormattedNotification = {
  id: string;
  content?: ReactNode;
  link: string;
  time?: string;
  status?: string;
  avatar?: {};
};

export type ProjectProcessStage = {
  id: string;
  stageName: string;
  enabled: boolean;
  description: string;
  process_id: string;
  data: object;
};

export type ProjectProcess = {
  id: string;
  enabled?: boolean;
  processName: string;
  projectStages: ProjectProcessStage[];
  description: string;
  organization_id: string;
  data: {
    createdBySystem?: boolean;
    createdBy?: {
      name?: string;
    };
  };
  stagesOrdering: string[];
  estimatedCompletionTime?: string;
  createdAt: string;
};

export type NewProjectProcessStage = {
  stageName: string;
  description: string;
  process_id?: string;
  data: object;
};

export type NewProjectProcess = {
  processName: string;
  description: string;
  organization_id: string;
  data: object;
  projectStages: NewProjectProcessStage[];
};

export type ProjectVendor = {
  id: string;
  vendorName: string;
  description?: string;
  enabled?: boolean;
  organization_id: string;
  country_iso_3166_1_alpha_2_code?: string;
  createdAt: string;
  data: {
    province?: string;
    address?: string;
    website?: string;
    contactName?: string;
    email?: string;
    phone?: string;
  };
};

export type ProjectCategory = {
  id: string;
  categoryName: string;
};

export type NotificationType =
  typeof NOTIFICATION_TYPE[keyof typeof NOTIFICATION_TYPE];

export type ProjectsListViewMode =
  typeof PROJECTS_LIST_VIEW_MODE[keyof typeof PROJECTS_LIST_VIEW_MODE];

export type ProjectsBoardTabs =
  typeof PROJECTS_BOARD_TABS[keyof typeof PROJECTS_BOARD_TABS];

export interface Option {
  label: string;
  value: any;
}

export interface StringOption extends Option {
  value: string;
}

export interface OptionWithKey extends Option {
  key: string;
}

export interface NewTask extends objKeyAsString {
  name: string;
  description?: string;
  start_date: string | null;
  due_date: string | null;
  estimate_hours?: string;
  organization_id?: string;
  parent_id?: string;
  project_id?: string;
}

export interface Task {
  id: string;
  name: string;
  description?: string;
  start_date?: string;
  due_date?: string;
  status?: Task_Status;
  completion_date?: string;
  estimate_hours?: string;
  actual_hours?: string;
  organization_id?: string;
  parent_id?: string;
  project_id?: string;
  createdAt: string;
  type?: string;
  assignedUsers?: TaskAssignedUser[];
  disabled?: boolean;
  created_by?: string;
}

export interface TaskDetailType extends objKeyAsString {
  name: string;
  description?: string;
  start_date?: string;
  due_date?: string;
  type?: string;
  task_assignee?: string;
  status?: Task_Status;
  estimate_hours?: string;
  completion_date?: string;
  actual_hours?: string;
  organization_id?: string;
  parent_id?: string;
  project_id?: string;
  createdAt: string;
  deletedAt: string;
  assignedUsers: Array<TaskAssignedUser> | string[];
  data: null;
  disabled: boolean;
  id: string;
  index: string;
  milestone_id: string;
  subTasks: Array<TaskDetailType>;
  updatedAt: string;
  created_by?: string;
  linkedContent?: ProjectContent[];
  linkedContentIds?: string[];
  taskActualHours?: TaskActualHours[];
}

export type TaskActualHours = {
  id?: string;
  task_id?: string;
  user_id: string;
  number_of_hours: number;
  actual_hours_date: string;
  actual_hours_creator?: object;
  updatedAt?: string;
};

export interface TaskAssignedUser {
  data: {
    companyName: string;
    email: string;
    firstName: string;
    freeTrial: boolean;
    jobTitle: string;
    lastName: string;
    resourceId: string;
    role: string;
  };
  id: string;
  task_assignedUsers: {
    createdAt: string;
    learnopsTaskId: string;
    updatedAt: string;
    userId: string;
  };
}

export type ProcessStage = {
  id: string;
  color: string;
  textColor: string;
  name: string;
  cards: Project[];
  colorOnDrag: string;
  borderColorOnDrag: string;
};

export type AvatarSize = 'small' | 'medium' | 'large';

export type ProjectStatus = typeof PROJECT_STATUS[keyof typeof PROJECT_STATUS];

export interface ProjectFile extends PickerFileMetadata {
  cloudFrontURL?: string;
}

export interface ProjectWeblink {
  handle: string;
  display: string;
  url: string;
}

export type ProjectContentType =
  typeof PROJECT_CONTENT_TYPE[keyof typeof PROJECT_CONTENT_TYPE];

export type ProjectContent = {
  id?: string;
  data: ProjectFile | ProjectWeblink | Design | object;
  content_type: ProjectContentType;
  uploader_user_id?: string;
  uploadedBy?: User;
  createdAt?: string;
  linkedTasks?: Task[];
  linkedTasksIds?: string[];
  linkedDesigns?: Design[];
  linkedDesignId?: string;
  name?: string;
};

export type TimeOffType = typeof TIME_OFF_TYPE[keyof typeof TIME_OFF_TYPE];

export interface TimeOff extends objKeyAsString {
  startDate?: string;
  endDate?: string;
  userId?: string;
  timeOffType?: TimeOffType;
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface TimeOffData extends objKeyAsString {
  id?: string;
  time_off_type?: TimeOffType;
  start_date: string;
  end_date: string;
  user_id?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type ProjectTasksTableTab =
  typeof TASKS_TABLE_TABS[keyof typeof TASKS_TABLE_TABS];

export type rangeDateType = {
  startDate: string;
  endDate: string;
};

export type ProjectTab =
  typeof UPDATE_PROJECT_TABS[keyof typeof UPDATE_PROJECT_TABS];

export interface Form extends Request {
  form_creator_id?: string;
  form_description?: string;
  createdAt?: string;
  updatedAt?: string;
  questions?: RequestQuestion[];
  isJsonSchema?: boolean;
  default_system_request_form?: boolean;
}

export interface NewFormType extends Form {
  organizationId: string;
}

export type Pagination = {
  limit: number;
  offset: number;
};
export type AllUsersType =
  | LDUser
  | BusinessUser
  | ExternalUser
  | FacilitatorUser;

export interface AvatarUser {
  avatar_url?: string;
  status?: string;
  data: {
    firstName: string;
    lastName: string;
    email?: string;
  };
}

export type HolidaysType = {
  name: string;
  date: string;
};

export interface NewBusinessTeamData {
  title: string;
  description: string;
}

export interface BudgetPlanType {
  estimated_cost: string | undefined;
  budget_source: string | null | undefined;
  allocated_budget: number | undefined;
}

export interface NewProjectParticipant {
  userId: string;
  startDate: string;
  endDate: string;
  job_role: string;
  type: string;
  hoursAssigned?: number;
  estimatedHours?: number;
}

export interface NewProjectCollaborator
  extends Omit<NewProjectParticipant, 'type' | 'hoursAssigned' | 'job_role'> {
  jobRole: string;
}

export type ResourcingType = 'internal' | 'mixed' | 'vendor';

export interface ResourcePlanType {
  resourcing_type: string | undefined | null;
  vendors: ProjectVendor[];
}

export interface Budget {
  id: string;
  category_id?: string;
  project_id?: string;
  category_name?: string;
  organization_id?: string;
  category_currency?: string;
  allocated_budget?: number;
  cost_to_date?: number;
  notes: string;
  createdAt: string;
}

export interface BudgetDetail {
  id: string;
  name?: string;
  organization_id?: string;
  currency?: string;
  createdAt: string;
  categoryBudgets?: Budget[];
}

export interface NewBudget extends objKeyAsString {
  category_id?: string;
  project_id?: string;
  category_name?: string;
  organization_id?: string;
  category_currency?: string;
  allocated_budget?: number;
  cost_to_date?: number;
  notes: string;
}

export interface BudgetCategory {
  id: string;
  name?: string;
  organization_id?: string;
  currency?: string;
}

export interface UserCapacity {
  data: {
    id: string;
    role: string;
    organization_id: string;
    ldTeam_id?: string | null;
    businessTeam_id?: string | null;
    type: 'business' | 'ld' | 'external';
    cost?: string | null;
    public_holidays_in_capacity_enabled: boolean;
    default_capacity: number;
    status: typeof USER_STATUS[keyof typeof USER_STATUS];
    country_iso_3166_1_alpha_2_code: string;
    location?: string | null;
    note?: string | null;
    job_title?: string | null;
    avatar_url?: string | null;
    skills?: string | null;
    data: {
      role: string;
      email: string;
      jobTitle: string;
      lastName: string;
      firstName: string;
      freeTrial: boolean;
      resourceId: string;
      companyName: string;
    };
    createdAt: string;
    updatedAt: string;
    deletedAt?: string | null;
    learning_team_members: {
      createdAt: string;
      updatedAt: string;
      learningTeamId: string;
      userId: string;
    };
  };
  allocations: {
    weekStart: string;
    weekEnd: string;
    capacity: number;
    timeOff: {
      weeklyTimeOffForUser: number;
      allocationTotal: number;
    };
    holidays: {
      date: string;
      start: string;
      end: string;
      name: string;
      type: string;
    }[];
    weeklyAllocationsByProjectAndRole: {
      [key: string]: { [key: string]: number };
    };
  }[];
  projects: {
    id: string;
    title: string;
    description: string;
    status: typeof PROJECT_STATUS[keyof typeof PROJECT_STATUS];
    priority: typeof PROJECT_PRIORITY[keyof typeof PROJECT_PRIORITY];
    health: ProjectHealth;
    participants: {
      id: string;
      project_participants: ProjectParticipant['project_participants'];
    }[];
    currentParticipantRoles: {
      role: string;
      startDate: string;
      endDate: string;
    }[];
    totalAllocationForAllRoles: number;
    startDate: string;
    targetCompletionDate: string;
  }[];
}

export interface CapacityEntry {
  users: Record<string, UserCapacity>;
  team: Omit<LearningTeam, 'ldTeamMembers'>;
}

export type dropdownOption = {
  value: string;
  display: string;
};

export type radioOption = {
  id: string;
  text: string;
};

export type checkboxOption = {
  index: string;
  item: string;
  checked: boolean;
};

export interface ResourceSummaryItem {
  projectRole: string;
  numberOfResource: number;
  totalHours: number;
  totalActualHours: number;
  collaboratorsResourceNumber: number;
  collaboratorsAllocatedHours: number;
}

export interface ResourceSummary {
  items: ResourceSummaryItem[];
  total: {
    totalNumberOfResource: number;
    totalHours: number;
    totalActualHours: number;
    totalCollaboratorsResourceNumber: number;
    totalCollaboratorsAllocatedHours: number;
  };
}

export interface TasksSummaryItem {
  taskType: string;
  numberOfResources: number;
  numberOfTasks: number;
  totalEstimatedHours: number;
  totalActualHours: number;
}

export interface TasksSummary {
  items: TasksSummaryItem[];
  total: {
    totalNumberOfTasks: number;
    totalNumberOfResources: number;
    totalEstimatedHours: number;
    totalActualHours: number;
  };
}

export interface ProjectCollaboratorsAllocations {
  start_date: string;
  end_date: string;
  estimated_hours: string;
  job_role: string;
  userId: string;
  projectLearnOpId: string;
  collaborator: Owner;
  assignedProjectTasks: TaskDetailType[];
}

export interface ParticipantAllocation {
  weekStart: string;
  weekEnd: string;
  capacity: number;
  allocatedHours: number;
  allocationId: string;
  role?: string;
  actualHours: number;
}

export interface AllocationUpdateData {
  allocatedHours: number;
  allocationId: string;
  actualHours: number;
}

export interface CollaboratorEstimation {
  start_date: string;
  end_date: string;
  estimated_hours: string;
  userId: string;
  projectLearnOpId: string;
}

export interface ProjectParticipantRole {
  role: string;
  participationId?: string;
  allocations?: ParticipantAllocation[];
  estimation?: CollaboratorEstimation;
  endDate?: string;
  startDate?: string;
  hoursAssigned?: number;
}

export interface TimeOffHoliday {
  date: string;
  start: string;
  end: string;
  name: string;
  type: string;
}

export interface WeekTimeOffAndHolidays {
  id?: string;
  holidaysWithinWeek: TimeOffHoliday[];
  weekEnd: string;
  weekStart: string;
  weeklyTimeOffForUser: number;
}

export interface ProjectParticipantAllocation {
  data: Owner;
  roles: ProjectParticipantRole[];
  assignedProjectTasks?: TaskDetailType[];
  holidaysAndTimeOffs?: WeekTimeOffAndHolidays[];
}

export interface ProjectParticipantsAllocations {
  [key: string]: ProjectParticipantAllocation;
}

export interface ResourceAllocationSections {
  [key: string]: {
    id: string;
    users: ProjectParticipantsAllocations;
  };
}

export interface UserIdWithName {
  userId: string;
  name: string;
}

export interface ParticipantAssignment {
  userId: string;
  startDate: string;
  endDate: string;
  job_role: string;
  totalAllocation?: number;
  participationId?: string;
  allocations?: ParticipantAllocation[];
  estimatedHours?: number;
  newlyAdded?: boolean;
  actualHours?: number;
}

export interface CollaboratorAssignment {
  userId: string;
  startDate: string;
  endDate: string;
  jobRole: string;
  estimatedHours?: number;
}

export type License = {
  license_tier: typeof LICENSE_TIER[keyof typeof LICENSE_TIER] | '';
  license_number: number;
};

export type TeamMemberProject = {
  id: string;
  title: string;
  priority: typeof PROJECT_PRIORITY[keyof typeof PROJECT_PRIORITY];
  health: ProjectHealth;
  roles: Record<string, string>;
  status: string;
  hours: number;
  startDate: string;
  targetCompletionDate: string;
  currentParticipantRoles: {
    role: string;
    startDate: string;
    endDate: string;
    weeks?: {
      startDate: string;
      endDate: string;
      hours: number;
    }[];
  }[];
};

export type TeamMemberAllocation = {
  weekStart: Date;
  weekEnd: Date;
  allocation: number;
  capacity: number;
  defaultCapacity: number;
  weeklyAllocationsByProjectAndRole?: {};
};

export interface UserColumnType {
  user: {
    roles: string;
    avatar_url?: string;
    data: UserData;
    isTeamManager?: boolean;
  };
  projects: Array<TeamMemberProject>;
}

export interface SectionUserType extends UserColumnType {
  id: string;
  allocations: Array<TeamMemberAllocation>;
  timeOffs: Array<{
    weekStart: Date;
    weekEnd: Date;
    capacity: number;
  }>;
}

export interface ResourceAllocationSectionsType {
  id: string;
  label: string;
  users: SectionUserType[];
}

export type ActiveHeadType = {
  orderBy: string;
  order: 'desc' | 'asc';
};

export type NewAssignmentFormValues = {
  startDate: string;
  endDate: string;
  job_role: string;
  totalAllocation: number;
};

export type GetUserAvailabilityParams = {
  timeFrameStartDate: string;
  timeFrameEndDate: string;
  userId: string;
};

export type BulkHandleAssignmentsChangesParams = {
  isCollaborator: boolean;
  updatedAssignments: ParticipantAssignment[];
  deletedAssignments: ParticipantAssignment[];
  addedParticipantAssignments: ParticipantAssignment[];
  projectId: string;
  userId: string;
};

export interface Organization {
  organizationData: OrganizationData;
  status: Status;
}

export interface OrganizationData {
  businessTeams: BusinessTeam[];
  learningTeams: LearningTeam[];
  projectCategories: ProjectCategory[];
  projectVendors: ProjectVendor[];
  users: User[];
  license_tier: License['license_tier'] | '';
  license_number: number;
  id: string;
  settings: OrganizationSettings;
  company_name: string;
  account_name?: string;
  trial_end_date?: string;
  hasConnectionId?: boolean;
  logout_redirect_url?: string | null;
}

export type PortalBackground = {
  image?: PickerFileMetadata;
  useDefault?: boolean;
};

export interface OrganizationSettings {
  teamRequestsTab?: boolean;
  platformCurrency?: string;
  intakePortalName?: string;
  portalLogo?: PickerFileMetadata;
  portalBackground?: PortalBackground;
}

export type ProjectPermissionsLevel =
  typeof PROJECT_PARTICIPANT_TYPE[keyof typeof PROJECT_PARTICIPANT_TYPE];

export type PortalSettings = {
  portalLogo?: PickerFileMetadata;
  portalBackground?: {
    image: PickerFileMetadata;
    useDefault: boolean;
  };
};

export type ColumnOption = {
  name: string;
  value: string;
  operators: {
    value: string;
    name: string;
  }[];
  type: string;
};

export type Design = {
  id: string;
  status: string;
  accountId: string;
  title: string;
  owner: string;
  config: object;
  collaborators: string[];
  tags: string[];
  published: string;
};

export type TableExportOptions =
  typeof TABLE_EXPORT_OPTIONS[keyof typeof TABLE_EXPORT_OPTIONS];

export type MentionType = {
  id: string;
  type: typeof MENTIONS_TYPES[keyof typeof MENTIONS_TYPES];
  content: string;
  authorName: string;
  title: string;
  link: string;
  createdAt: string;
  mentions?: Mentions;
};

export interface Program {
  title: string;
  id: string;
  description?: string;
  status: string;
  programOwners: Owner[];
  createdAt: string;
  delivery_type: 'online' | 'in_person' | 'blended' | null;
  program_number: number | null;
  programProjects: ProgramLinkedProject[];
  program_creator_id: string;
  is_archived?: boolean;
  archive_date?: string | null;
  projectDetails?: Project[];
  updatedProjects?: ProgramLinkedProject[];
}

export type DateFilter = { from: string; to?: string };

export type ProjectFiltersKey =
  typeof PROJECTS_TABLE_FILTERS[keyof typeof PROJECTS_TABLE_FILTERS];

export type ProjectFilters = {
  [PROJECTS_TABLE_FILTERS.STATUS]: string[];
  [PROJECTS_TABLE_FILTERS.PRIORITY]: string[];
  [PROJECTS_TABLE_FILTERS.OWNERS]: string[];
  [PROJECTS_TABLE_FILTERS.HEALTH]: string[];
  [PROJECTS_TABLE_FILTERS.BUSINESS_TEAMS]: string[];
  [PROJECTS_TABLE_FILTERS.PRIVACY]: string[];
  [PROJECTS_TABLE_FILTERS.TEAMS]: string[];
  [PROJECTS_TABLE_FILTERS.CATEGORY]: string[];
  [PROJECTS_TABLE_FILTERS.START_DATE]: DateFilter;
  [PROJECTS_TABLE_FILTERS.TARGET_COMPLETION_DATE]: DateFilter;
  [PROJECTS_TABLE_FILTERS.ACTUAL_COMPLETION_DATE]: DateFilter;
  [PROJECTS_TABLE_FILTERS.RESOURCING_TYPE]: string[];
  [PROJECTS_TABLE_FILTERS.BUDGET_SOURCE]: string[];
  [PROJECTS_TABLE_FILTERS.PROCESS]: string[];
  [PROJECTS_TABLE_FILTERS.STAGE]: string[];
  [PROJECTS_TABLE_FILTERS.ARCHIVED]: string[];
};

export type FieldsFilters = {
  [CUSTOM_FIELDS_FILTERS.TYPE]: string[];
};

export type FieldsFiltersKey =
  typeof CUSTOM_FIELDS_FILTERS[keyof typeof CUSTOM_FIELDS_FILTERS];

export type AppliedFilter = {
  key: string;
  value: string;
  label: string;
};

export type FieldsFiltersQueryParams = {
  value_selector: string;
  type?: string[];
};

export function isDateFilter(value: any): value is DateFilter {
  return typeof value === 'object' && ('from' in value || 'to' in value);
}

export interface UserSettings {
  id: string;
  user_id: string;
  draft_public_setting: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface UserViewSettings {
  view_settings_id: string;
  view_settings_values_id: string;
  setting_key: string;
  value: string;
  type: string;
  is_default: boolean;
}

export type DeleteProgramFlag =
  typeof DELETE_PROGRAM_OPTIONS[keyof typeof DELETE_PROGRAM_OPTIONS];

export interface GeneralInsights {
  status?: string;
  total: number;
  new: number;
  canceled: number;
  completed: number;
  completedWithinTimeline: number;
}

export interface ApprovedRequestAnalytic {
  businessTeamTitle: string;
  requestCount: number;
  businessTeamId?: string;
}

export interface DeclinedRequestAnalytic {
  declinationReason: string;
  requestCount: number;
}

export interface InReviewRequestAnalytic {
  daysInReview: number;
  requestId: string;
}

export interface InReviewRequestBarChartData {
  name: string;
  count: number;
}

export interface RequestsBy {
  status: string;
  values: { [name: string]: string | number }[];
  seriesNames: string[];
}

export interface BusinessUnitFilterData {
  businessUnitId: string;
  businessUnitName: string | null;
}

export interface FormFilterData {
  formId: string;
  formTitle: string | null;
}

export interface OwnerFilterData {
  ownerId: string | null;
  ownerName: string | null;
}

export interface TypeFilterData {
  requestType: string;
}

export interface InsightsIntakeActiveFilter {
  startDate?: string;
  ownerIds?: string[];
  formIds?: string[];
  businessUnitIds?: string[];
  requestTypes?: string[];
}

export interface InsightsProjectActiveFilter {
  startDate?: string;
  priorities?: string[];
  businessUnitIds?: string[];
  processNames?: string[];
}

export interface InsightsCapacityActiveFilter {
  startDate?: string;
  endDate?: string;
  businessUnitIds?: string[];
}

export interface PriorityFilterData {
  projectPriority: string;
}

export interface ProcessFilterData {
  processId: string | null;
  processName: string | null;
}

export type OptionLinkRequest = Option & { requestNumber: string };

export type PageSizeType = 5 | 10 | 15 | 20 | 25 | 50 | 100;

export type BundleCreator = {
  id: string;
  avatar_url?: string;
  data: {
    email: string;
    firstName: string;
    lastName: string;
  };
};

export type TaskBundle = {
  name: string;
  description: string;
  creator_id: string;
  taskBundleCreator: BundleCreator | null;
  createdAt: string;
  id: string;
  totalEstimatedHours: string;
};

export type TaskBundleCreationData = {
  name: string;
  description: string;
};

export type DropdownRequest = {
  id: string;
  title: string;
  requestIdentifier: string;
  requester: {
    firstName: string;
    lastName: string;
    avatrURL?: string;
  };
  businessTeam: {
    title: string;
  };
};

export type DropdownBusinessTeam = {
  id: string;
  name: string;
};

export interface Card {
  id?: string; // optional for local-only cards
  lid?: string; // local id for unsaved cards
  cardTypeId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  dashboardId: string;
}

export interface DashboardOwner {
  id: string;
  externalId: string;
  organizationId: string;
  externalOrganizationId: string;
  status: string;
  firstName: string;
  lastName: string;
  email: string;
  disabled?: boolean;
}

export interface DashboardViewer {
  id: string;
  externalId: string;
  organizationId: string | null;
  externalOrganizationId: string;
  version: number;
  type: string;
  status: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  disabledAt: string | null;
  sharedDashboardIds: string[];
}

export interface AgentDashboardShare {
  id: string;
  email: string;
  createdAt: string;
}

export enum DashboardUserPermission {
  OWNER = 'owner',
  VIEWER = 'viewer',
}
export type DashboardAffordancesType = {
  global: Partial<Record<DashboardUserAffordance, string>>;
  resources: DashboardAffordancesResources;
};

export interface DashboardAffordancesResources {
  [key: string]: Partial<Record<DashboardUserAffordance, string>>;
}

interface UnlockedDashboardFields {
  createdAt: string;
  updatedAt: string;
}

export interface Dashboard extends UnlockedDashboardFields {
  id: string;
  name: string;
  permission?: DashboardUserPermission;
  owners?: DashboardOwner[];
  conversationId?: string;
}

export type NewDashboard = Pick<Dashboard, 'name'>;

export type LockedFieldKeys = Exclude<
  keyof Dashboard,
  keyof UnlockedDashboardFields
>;

export interface DashboardWithRelationships {
  dashboard: Dashboard;
  cards?: Card[];
  filters?: DashboardFilters;
  resourceAffordances?: Record<string, string>;
}

export type InsightsApi = typeof INSIGHT_IDS[keyof typeof INSIGHT_IDS];

export interface CardType {
  id: string;
  insightId: InsightCatalogue;
  name: string;
  description: string;
  defaultWidth: number;
  defaultHeight: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  dataUrl: string;
  category: InsightCategory;
  chartType: ChartType;
}

export interface ScheduleDeliveryDashboard {
  sendTo: string[];
  date: string;
  time: string;
  repeatMode: string;
  repeatEvery: number;
  repeatOn: string;
  week: string;
  days: string[];
  ends: boolean;
  endDate: string;
}

export interface DashboardDetailContextType {
  insightCharts: CardType[];
}

export enum PilotPagesToTrack {
  UNKNOWN = 'unknown',
  PROJECT_TASK = 'project-task',
  CENTRALIZED_TASK = 'centralized-task',
  HOME = 'home',
  CENTRALIZED_PROJECT = 'centralized-project',
  PROGRAM_DETAIL = 'program-detail',
  PROGRAM_LIST = 'program-list',
  REQUEST_LIST = 'request-list',
  GOAL_DETAIL = 'goal-detail',
}

export enum StatusInlineEditingEntityType {
  TASK = 'task',
  PROJECT = 'project',
  PROGRAM = 'program',
}
